import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Camera, CameraOff, CheckCircle2, Plus, ScanLine, Trash2, Users, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { inputCls, btnPrimary, useApi, Skeleton } from "@/components/portal/ui";
import { AdminTitle } from "@/pages/admin/AdminLayout";
import { QrScanner } from "@/components/portal/QrScanner";

const sel = `${inputCls} !py-2.5 text-sm`;
const EMPTY = { title: "", date: "", time: "19:00", location: "", note: "" };

function AttendeesModal({ meetingId, onClose }) {
  const { data } = useApi(`/admin/internal-meetings/${meetingId}/attendees`);
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4" data-testid="im-attendees-modal" onClick={onClose}>
      <div className="w-full max-w-md bg-white rounded-3xl overflow-hidden shadow-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-slate-900 text-white p-5 flex items-start justify-between">
          <div><p className="text-[11px] font-black uppercase tracking-widest text-amber-300">Peserta</p><p className="font-extrabold mt-0.5">{data?.meeting?.title || "Memuat…"}</p></div>
          <button onClick={onClose} data-testid="im-attendees-close" className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">
          {!data ? <Skeleton className="h-24" /> : data.attendees.length === 0 ? (
            <p className="text-sm text-slate-400">Belum ada yang mendaftar.</p>
          ) : (
            <div className="divide-y divide-slate-50">
              {data.attendees.map((a) => (
                <div key={a.id} className="py-2.5 flex items-center justify-between" data-testid={`im-attendee-${a.id}`}>
                  <span className="font-bold text-slate-900 text-sm">{a.name}</span>
                  <span className={`text-[11px] font-bold rounded-full px-2.5 py-1 ${a.scanned_at ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{a.scanned_at ? "Hadir" : "Terdaftar"}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Scanner() {
  const [value, setValue] = useState("");
  const [camera, setCamera] = useState(false);
  const [last, setLast] = useState(null);
  const submit = async (raw) => {
    const code = (raw || "").trim();
    if (!code) return;
    try {
      const { data } = await api.post("/portal/internal-meetings/scan", { code });
      setLast({ name: data.name, meeting: data.meeting_title });
      toast.success(`Hadir dicatat · ${data.name}`);
      setValue("");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="bg-white rounded-3xl p-5 border border-slate-100 space-y-3" data-testid="im-scanner">
      <p className="font-extrabold text-slate-900 text-sm flex items-center gap-2"><ScanLine className="w-4 h-4" />Pindai Absensi Rapat</p>
      <form onSubmit={(e) => { e.preventDefault(); submit(value); }} className="flex gap-2">
        <input className={sel} value={value} onChange={(e) => setValue(e.target.value)} placeholder="Kode tiket (IM-…)" data-testid="im-scan-input" />
        <button className={`${btnPrimary} !py-2.5`} data-testid="im-scan-submit"><ScanLine className="w-4 h-4" /></button>
      </form>
      <button type="button" onClick={() => setCamera(!camera)} data-testid="im-camera-toggle" className="w-full inline-flex items-center justify-center gap-2 border border-slate-200 rounded-xl py-2.5 text-sm font-bold text-slate-700 active:scale-95 transition">
        {camera ? <><CameraOff className="w-4 h-4" />Matikan Kamera</> : <><Camera className="w-4 h-4" />Scan QR dengan Kamera</>}
      </button>
      {camera && <QrScanner onScan={submit} testId="im-qr-reader" />}
      {last && (
        <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4 flex items-center gap-3" data-testid="im-scan-result">
          <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
          <div className="min-w-0"><p className="font-bold text-emerald-900 truncate">{last.name}</p><p className="text-xs text-emerald-700 truncate">{last.meeting}</p></div>
        </div>
      )}
    </div>
  );
}

export default function InternalMeetings() {
  const { data: meetings, reload } = useApi("/admin/internal-meetings");
  const { data: mine, reload: reloadMine } = useApi("/portal/internal-meetings");
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [attendeesOf, setAttendeesOf] = useState(null);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/internal-meetings", form);
      toast.success("Rapat dibuat & staf dinotifikasi");
      setForm(EMPTY);
      reload();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/internal-meetings/${id}`); toast.success("Rapat dihapus"); reload(); } catch (e2) { toast.error(errMsg(e2)); }
  };
  const register = async (id) => {
    try { await api.post(`/portal/internal-meetings/${id}/register`); toast.success("Terdaftar! Tiket QR-mu sudah siap"); reloadMine(); } catch (e2) { toast.error(errMsg(e2)); }
  };
  const ticketOf = (id) => (mine?.meetings || []).find((m) => m.id === id);

  return (
    <div data-testid="admin-internal-meetings">
      <AdminTitle eyebrow="Staf & Pengurus" title="Pertemuan Internal" />
      <div className="grid lg:grid-cols-[22rem_1fr] gap-6">
        <div className="space-y-6 self-start">
          <form onSubmit={create} className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 space-y-3" data-testid="im-create-form">
            <p className="font-extrabold text-slate-900">Buat Rapat Staf</p>
            <input required className={sel} placeholder="Judul rapat" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="im-title-input" />
            <input required type="date" className={sel} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="im-date-input" />
            <input type="time" className={sel} value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="im-time-input" />
            <input className={sel} placeholder="Lokasi" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="im-location-input" />
            <textarea rows={2} className={sel} placeholder="Catatan (opsional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="im-note-input" />
            <button disabled={busy} className={`${btnPrimary} w-full !py-2.5`} data-testid="im-create-submit"><Plus className="w-4 h-4" />Buat & Notifikasi Staf</button>
          </form>
          <Scanner />
        </div>

        <div className="space-y-3">
          {!meetings ? <Skeleton className="h-64" /> : meetings.length === 0 ? (
            <div className="bg-white rounded-3xl p-10 text-center border border-slate-100" data-testid="im-empty">
              <Users className="w-8 h-8 mx-auto text-slate-300" />
              <p className="mt-3 text-sm text-slate-500">Belum ada rapat staf dibuat.</p>
            </div>
          ) : meetings.map((m) => {
            const t = ticketOf(m.id);
            return (
              <div key={m.id} className="bg-white rounded-3xl p-5 border border-slate-100" data-testid={`im-meeting-${m.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-extrabold text-slate-900">{m.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{fmtDate(m.date, { weekday: "long", day: "numeric", month: "long" })} · {m.time} WITA{m.location ? ` · ${m.location}` : ""}</p>
                    {m.note && <p className="text-xs text-slate-400 mt-1">{m.note}</p>}
                  </div>
                  <button onClick={() => remove(m.id)} data-testid={`im-delete-${m.id}`} className="shrink-0 w-8 h-8 rounded-lg bg-slate-100 text-red-600 grid place-items-center hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] font-bold bg-blue-100 text-blue-700 rounded-full px-3 py-1">{m.registered} terdaftar</span>
                  <span className="text-[11px] font-bold bg-emerald-100 text-emerald-700 rounded-full px-3 py-1">{m.present} hadir</span>
                  <button onClick={() => setAttendeesOf(m.id)} data-testid={`im-view-attendees-${m.id}`} className="text-[11px] font-bold text-slate-500 hover:text-slate-800">Lihat Peserta</button>
                  {!t?.ticket_code && <button onClick={() => register(m.id)} data-testid={`im-register-${m.id}`} className="ml-auto text-[11px] font-bold text-indigo-600 bg-indigo-50 rounded-full px-3 py-1.5 hover:bg-indigo-100">Daftar (Saya Hadir)</button>}
                </div>
                {t?.ticket_code && (
                  <div className="mt-3 rounded-2xl bg-slate-900 text-white p-4 flex items-center gap-4" data-testid={`im-ticket-${m.id}`}>
                    <span className="bg-white p-2 rounded-xl shrink-0"><QRCodeSVG value={`GIM-IM:${t.ticket_code}`} size={72} /></span>
                    <div className="min-w-0">
                      <p className="text-[10px] font-black uppercase tracking-widest text-amber-300">Tiket Rapat Saya</p>
                      <p className="text-[11px] text-white/50 break-all mt-1">{t.ticket_code}</p>
                      {t.scanned_at && <p className="mt-1 text-[10px] font-bold text-emerald-300">Kehadiran tercatat</p>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {attendeesOf && <AttendeesModal meetingId={attendeesOf} onClose={() => setAttendeesOf(null)} />}
    </div>
  );
}
